import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Lock, LogIn, AlertCircle } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

export default function Login() {
  const { doLogin } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await doLogin(email.trim().toLowerCase(), password);
      navigate("/", { replace: true });
    } catch (err) {
      if (!err?.response) {
        // No HTTP response = timeout / network (retries already exhausted in api.js).
        setError("Server is busy — please try again in a moment.");
      } else {
        const detail = err?.response?.data?.detail;
        setError(typeof detail === "string" ? detail : "Login failed. Check your credentials.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-black text-zinc-100 flex items-center justify-center px-4"
      data-testid="login-page"
    >
      <div className="w-full max-w-sm border border-zinc-800 bg-[#09090B]">
        <div className="border-b border-zinc-800 px-5 py-4 flex items-center gap-2">
          <span className="inline-block h-2 w-2 bg-amber-500 blink" aria-hidden />
          <span className="text-sm font-semibold tracking-[0.18em] uppercase text-amber-term">
            COT.TERMINAL
          </span>
        </div>

        <form onSubmit={onSubmit} className="px-5 py-6 space-y-4">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-zinc-500">
            <Lock size={12} strokeWidth={1.5} /> Restricted access · authorized users only
          </div>

          <div className="space-y-1.5">
            <label className="text-[11px] uppercase tracking-widest text-zinc-500">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="username"
              required
              data-testid="login-email"
              className="w-full bg-black border border-zinc-700 px-3 py-2 text-sm font-mono text-zinc-100 focus:outline-none focus:border-amber-500"
              placeholder="owner@cot.terminal"
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-[11px] uppercase tracking-widest text-zinc-500">Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              required
              data-testid="login-password"
              className="w-full bg-black border border-zinc-700 px-3 py-2 text-sm font-mono text-zinc-100 focus:outline-none focus:border-amber-500"
              placeholder="••••••••"
            />
          </div>

          {error && (
            <div
              className="flex items-center gap-2 text-xs text-neg border border-red-500/40 bg-red-950/20 px-3 py-2"
              data-testid="login-error"
            >
              <AlertCircle size={13} strokeWidth={1.5} /> {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            data-testid="login-submit"
            className="w-full inline-flex items-center justify-center gap-2 border border-amber-500/60 bg-amber-950/20 px-3 py-2 text-xs uppercase tracking-widest text-amber-term hover:bg-amber-900/30 disabled:opacity-50 transition-colors"
          >
            <LogIn size={13} strokeWidth={1.5} />
            {loading ? "Authenticating…" : "Enter Terminal"}
          </button>
        </form>
      </div>
    </div>
  );
}
